import { useSyncExternalStore } from 'react'

/**
 * Module-level registry of in-flight bulk async job ids being polled.
 *
 * Replaces the `getAsyncTransactionUpdateJobStatus` Redux thunk as the
 * cross-component trigger for the bulk-update status poll. The three legacy
 * trigger sites (the App.tsx Pusher `AsyncJob` handler and the two bulk-update
 * mutation `onSuccess` callbacks) call `startAsyncJobPoll(jobId)`; the
 * `<AsyncJobPollers>` host renders one poller per active id and calls
 * `stopAsyncJobPoll(jobId)` when that job reaches a terminal state.
 *
 * This is client UI state (a set of ids), not server state, so it lives in a
 * small external store rather than React Query or Redux. Multiple ids may be
 * active at once (concurrent bulk jobs), mirroring the legacy bulk-job map.
 */
let activeJobIds: ReadonlyArray<string> = []
const listeners = new Set<() => void>()

const emit = (): void => {
  listeners.forEach((listener) => listener())
}

export const startAsyncJobPoll = (jobId: string): void => {
  if (activeJobIds.includes(jobId)) {
    return
  }
  activeJobIds = [...activeJobIds, jobId]
  emit()
}

export const stopAsyncJobPoll = (jobId: string): void => {
  if (!activeJobIds.includes(jobId)) {
    return
  }
  activeJobIds = activeJobIds.filter((id) => id !== jobId)
  emit()
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getSnapshot = (): ReadonlyArray<string> => activeJobIds

export const useActiveAsyncJobIds = (): ReadonlyArray<string> =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
